import { Component, CUSTOM_ELEMENTS_SCHEMA, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule } from '@ngx-translate/core';
import { QuestionnaireNotificationModalComponent } from '../../dialogs/questionaire-notification-modal';
import { Questionnaire, QuestionnaireNotification } from '../../models/assessment-questionaire.model';
import { CepConfigurationModel } from '../../models/cep-configuration.model';
import { CepPaginationMode } from '../../models/cep-pagination-mode.model';
import { CepTranslationModel } from '../../models/cep-translation.model';
import { AppConfigService } from '../../services/app-config-service/app-config.service';
import { AssessmentService } from '../../services/assessment-service/assessment.service';
import { AuthenticationService } from '../../services/authentication-service/authentication.service';
import { UserContextService } from '../../services/user-context-service/user-context.service';

@Component({
  selector: 'app-assessment',
  templateUrl: './assessment.component.html',
  imports: [RouterModule, TranslateModule, FormsModule],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AssessmentComponent implements OnDestroy, OnInit {
  activeAssessmentId = '';
  questionnaireConfig: CepConfigurationModel;
  isRtl = false;
  language = 'en';
  translations: CepTranslationModel[];
  paginationMode: CepPaginationMode;
  activePageId: any;
  applicationData: any;
  @ViewChild('questionnaireComponent') questionnaire: ElementRef;
  renewToken: Function;
  summaryMode: boolean = false;
  summaryModeOnPageLoad: boolean = false;
  insuredPersonFullName: string;
  showPaginationBreadcrumb: boolean;
  showFullPagePaginationBar: boolean;
  cepButtonDisabledOption: string;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly assessmentService: AssessmentService,
    private readonly modalService: NgbModal,
    private readonly router: Router,
    private readonly appConfig: AppConfigService,
    private readonly authenticationService: AuthenticationService,
    private readonly userContextService: UserContextService
  ) {}

  ngOnInit(): void {
    this.activeAssessmentId = this.route.snapshot.paramMap.get('assessmentId');
    this.language = this.route.snapshot.paramMap.get('lang');
    const possibleTranslationFiles: CepTranslationModel[] = this.appConfig.cepTranslation.filter(
      translation => translation.localeKey === this.language
    );
    this.isRtl = possibleTranslationFiles.length === 1 ? possibleTranslationFiles[0].translationTable.dir === 'rtl' : false;
    this.translations = this.appConfig.cepTranslation;
    this.cepButtonDisabledOption = this.appConfig.configData?.assessmentPage?.cepButtonShouldBeDisabled ?? 'invalid';
    this.questionnaireConfig = this.appConfig.cepConfiguration;
    if (this.route.snapshot.paramMap.get('readonly') === 'true') {
      this.summaryModeOnPageLoad = true;
    }
    this.renewToken = (): Promise<null | void> => {
      return this.authenticationService.refreshCepTokenAsPromise(this.activeAssessmentId);
    };
    this.paginationMode = this.appConfig.configData.questionnaire?.paginationMode || CepPaginationMode.Questionnaire;
    this.showPaginationBreadcrumb =
      this.paginationMode === CepPaginationMode.QuestionGroup &&
      (this.appConfig.configData.questionnaire?.showPaginationBreadcrumb ?? true);
    this.showFullPagePaginationBar =
      this.paginationMode === CepPaginationMode.QuestionGroup &&
      (this.appConfig.configData.questionnaire?.showFullPagePaginationBar ?? true);
    this.insuredPersonFullName = this.userContextService.getInsuredPersonFullNameForAssessmentWrapper(
      this.assessmentService.getCurrentAssessmentWrapperId()
    );
  }

  ngOnDestroy() {
    this.assessmentService.publishQuestionnaire(undefined);
  }

  onNotification(notificationData: QuestionnaireNotification) {
    if (notificationData.type && notificationData.type === 'error') {
      if (notificationData.error?.status > 500) {
        this.router.navigate(['/error']);
      } else {
        const modalRef = this.modalService.open(QuestionnaireNotificationModalComponent, {
          ariaLabelledBy: 'modal-title',
          ariaDescribedBy: 'modal-body',
          centered: true,
        });
        modalRef.componentInstance.headline = notificationData.headline;
        modalRef.componentInstance.contentText = notificationData.messagetext;
        modalRef.componentInstance.messageType = notificationData.type;
      }
    }
  }

  onQuestionnaireInit() {
    if (this.questionnaire) {
      this.cepInitialSummaryModeWorkaround();
      this.assessmentService.publishQuestionnaire(<Questionnaire>this.questionnaire.nativeElement);
    }
  }

  onAssessmentFinished() {
    this.assessmentService.completeAssessment(this.assessmentService.getCurrentAssessmentWrapperId()).subscribe({
      next: () => {
        this.scrollToTop();
        this.router.navigate(['/confirm', this.activeAssessmentId, { lang: this.language }]).then();
      },
      error: () => {
        this.router.navigateByUrl('/error').then();
      },
      complete: () => {},
    });
  }

  onSummaryModeChange(newSummaryMode: boolean) {
    this.summaryMode = newSummaryMode;
    this.scrollToTop();
  }

  private scrollToTop() {
    window.setTimeout(() => window.scroll(0, 0));
  }

  // an initialization of the CEP questionnaire in summary mode is not possible as of CEP-22.0.0-RC6
  // so we have to wait for the questionnaire initialize event and instantly activate the summary mode
  // cases: come back from the confirm page to the assessment page
  private cepInitialSummaryModeWorkaround(): void {
    if (this.summaryModeOnPageLoad) {
      this.summaryMode = true;
    }
  }
}
